import React, { useState, useEffect } from 'react';
import { TextField, Paper, Typography, Box, FormControlLabel, Checkbox, MenuItem, Chip } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { useSnackbar } from '../../components/SnackbarProvider';
import MyButton from '../MyButton';
import { ButtonType, GeneriqueDataType, CATEGORISABLE_TYPES, ImpactResultat, PorteeGenerique } from '../../constants/enums';
import SelectCategorie from '../Selects/SelectCategorie';

import {
    createGeneriqueData,
    deleteGeneriqueData,
    setSelectedGeneriqueData,
} from '../../reduxStore/GeneriqueDataSlice';

// donnees_supplementaires arrive en chaîne JSON (colonne LONGTEXT) — parse défensif.
const parseDonnees = (raw: any): Record<string, any> => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch { return {}; }
};

interface GeneriqueDataFormProps {
    onCancel: () => void;
    typeDonnee: GeneriqueDataType;
    onSuccess: () => void;
}

const GeneriqueDataForm: React.FC<GeneriqueDataFormProps> = ({ onCancel, typeDonnee, onSuccess }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { showSnackbar } = useSnackbar();
    const { selectedGeneriqueData } = useSelector((state: any) => state.GeneriqueDataSlice);
    const { types: sysTypes } = useSelector((state: any) => state.SysGeneriqueDataTypeSlice);

    // Portée du type courant : un type GLOBAL est commun à tous les départements
    // (departement_id forcé NULL par le backend) → on l'indique en lecture seule.
    const isGlobalType =
        sysTypes.find((t: any) => t.code === typeDonnee)?.portee === PorteeGenerique.GLOBAL;

    const getInitialData = () => ({
        id: -1,
        type_donnee: typeDonnee,
        nom: '',
        visible: true,
        // Lien nature → catégorie (utilisé uniquement pour le type NATURE_REGLEMENT).
        categorie_id: null as number | null,
        famille_row_id: null as number | null,
        // Impact résultat — champ de travail à plat, extrait de donnees_supplementaires
        // (utilisé uniquement pour le type CATEGORIE_NATURE_REGLEMENT).
        impact_resultat: ImpactResultat.NEUTRE as ImpactResultat,
    });

    const [formData, setFormData] = useState(getInitialData);

    useEffect(() => {
        if (selectedGeneriqueData) {
            // À l'édition, on extrait impact_resultat du JSON donnees_supplementaires.
            const ds = parseDonnees(selectedGeneriqueData.donnees_supplementaires);
            setFormData({
                ...selectedGeneriqueData,
                impact_resultat: ds.impact_resultat ?? ImpactResultat.NEUTRE,
            });
        } else {
            setFormData(getInitialData());
        }
    }, [selectedGeneriqueData, typeDonnee]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked }));
    };

    const resetForm = () => {
        dispatch(setSelectedGeneriqueData(null));
        setFormData(getInitialData());
    };

    const handleSubmit = async () => {
        try {
            const typeEntry = sysTypes.find((t: any) => t.code === typeDonnee);
            const payload: any = {
                ...formData,
                type_donnee: typeDonnee,
                type_donnee_id: typeEntry?.id,
            };
            // L'impact n'existe que pour les catégories de nature de règlement →
            // on l'emballe dans donnees_supplementaires et on retire le champ de travail.
            if (typeDonnee === GeneriqueDataType.CATEGORIE_NATURE_REGLEMENT) {
                payload.donnees_supplementaires = { impact_resultat: formData.impact_resultat };
            }
            delete payload.impact_resultat;
            await dispatch(createGeneriqueData(payload)).unwrap();
            showSnackbar('Opération réussie!', 'success');
            onSuccess();
            resetForm();
        } catch (error) {
            showSnackbar(error as string || 'Erreur lors de l\'enregistrement', 'error');
        }
    };

    const handleDelete = async () => {
        if (selectedGeneriqueData && window.confirm('Confirmez-vous la suppression ?')) {
            try {
                await dispatch(deleteGeneriqueData(selectedGeneriqueData.id)).unwrap();
                showSnackbar('Donnée supprimée', 'success');
                onSuccess();
                resetForm();
            } catch (error) {
                showSnackbar(error as string || 'Échec de la suppression', 'error');
            }
        }
    };

    return (
        <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
            <Typography variant="h6" gutterBottom>
                {selectedGeneriqueData ? 'Modifier une Donnée Générique' : 'Ajouter une Donnée Générique'}
            </Typography>
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} alignItems="center">
                    <TextField
                        name="nom"
                        label="Nom"
                        value={formData.nom ?? ''}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        sx={{ gridColumn: 'span 2' }}
                    />

                    {isGlobalType && (
                        <Chip
                            icon={<PublicIcon />}
                            label="Commun à tous les départements"
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ gridColumn: 'span 2', justifySelf: 'start' }}
                        />
                    )}

                    {CATEGORISABLE_TYPES[typeDonnee] && (
                        <Box sx={{ gridColumn: 'span 2' }}>
                            <SelectCategorie
                                categoryType={CATEGORISABLE_TYPES[typeDonnee]!}
                                value={formData.categorie_id ?? null}
                                onChange={(catId) =>
                                    setFormData(prev => ({ ...prev, categorie_id: catId }))
                                }
                            />
                        </Box>
                    )}

                    {/* Impact résultat — uniquement pour les catégories de nature de règlement */}
                    {typeDonnee === GeneriqueDataType.CATEGORIE_NATURE_REGLEMENT && (
                        <TextField
                            select
                            name="impact_resultat"
                            label="Impact dans le résultat"
                            value={formData.impact_resultat ?? ImpactResultat.NEUTRE}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            sx={{ gridColumn: 'span 2' }}
                        >
                            <MenuItem value={ImpactResultat.PRODUIT}>Produit (revenu)</MenuItem>
                            <MenuItem value={ImpactResultat.CHARGE}>Charge (dépense)</MenuItem>
                            <MenuItem value={ImpactResultat.NEUTRE}>Neutre (exclu du calcul)</MenuItem>
                        </TextField>
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.visible}
                                onChange={handleCheckboxChange}
                                name="visible"
                                color="primary"
                            />
                        }
                        label="Visible"
                        sx={{ gridColumn: 'span 2' }}
                    />
                </Box>

                <Box mt={2} display="flex" justifyContent="space-between">
                    <div>
                        <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleSubmit}>
                            {selectedGeneriqueData ? 'Modifier' : 'Ajouter'}
                        </MyButton>
                        <MyButton actionType={ButtonType.DELETE} variant="outlined" onClick={handleDelete}>
                            Supprimer
                        </MyButton>
                        <MyButton
                            actionType={ButtonType.CANCEL}
                            variant="outlined"
                            onClick={() => { resetForm(); onCancel(); }}
                        >
                            Annuler
                        </MyButton>
                    </div>
                </Box>
            </form>
        </Paper>
    );
};

export default GeneriqueDataForm;
